import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Target, Plus, Trash2, Tv, RefreshCw, QrCode } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const PERIODS = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
];

const computeWindow = (period) => {
  const today = new Date();
  if (period === 'daily') {
    const iso = today.toISOString().slice(0, 10);
    return { start: iso, end: iso };
  }
  if (period === 'monthly') {
    const start = new Date(today.getFullYear(), today.getMonth(), 1);
    const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return { start: start.toISOString().slice(0, 10), end: end.toISOString().slice(0, 10) };
  }
  // weekly (Mon-Sun)
  const d = new Date(today);
  const day = (d.getDay() + 6) % 7; // Mon=0
  const monday = new Date(d); monday.setDate(d.getDate() - day);
  const sunday = new Date(monday); sunday.setDate(monday.getDate() + 6);
  return { start: monday.toISOString().slice(0, 10), end: sunday.toISOString().slice(0, 10) };
};

const ProductionProgressWidget = ({ tvLink = true }) => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [period, setPeriod] = useState('weekly');
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(false);
  const [productTypes, setProductTypes] = useState([]);
  const [setOpen, setSetOpen] = useState(false);
  const [form, setForm] = useState({ target_count_total: 10, by_product_type: [], notes: '' });

  const isManager = user?.role === 'Admin' || user?.role === 'ProductionSupervisor';

  const fetchProgress = async () => {
    setLoading(true);
    try {
      const [pRes, ptRes] = await Promise.all([
        api().get(`/dashboard/production-progress?period_type=${period}`),
        api().get('/product-types').catch(() => ({ data: [] })),
      ]);
      setProgress(pRes.data);
      setProductTypes(ptRes.data);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchProgress(); /* eslint-disable-next-line */ }, [period]);

  const ptMap = useMemo(() => productTypes.reduce((acc, p) => { acc[p.id] = p; return acc; }, {}), [productTypes]);
  const win = progress?.window;
  const totals = progress?.totals || {};
  const completionRate = totals.completion_rate;
  const onPace = totals.delta_vs_pace !== null && totals.delta_vs_pace >= 0;

  const openSetTarget = () => {
    const w = computeWindow(period);
    setForm({
      target_count_total: progress?.target?.target_count_total || 10,
      by_product_type: progress?.target?.by_product_type || [],
      period_start: w.start,
      period_end: w.end,
      notes: progress?.target?.notes || '',
    });
    setSetOpen(true);
  };

  const saveTarget = async () => {
    try {
      const w = computeWindow(period);
      const payload = {
        period_type: period,
        period_start: form.period_start || w.start,
        period_end: form.period_end || w.end,
        target_count_total: parseInt(form.target_count_total) || 0,
        by_product_type: (form.by_product_type || []).filter(b => b.product_type_id && b.target > 0),
        notes: form.notes || null,
        active: true,
      };
      if (progress?.target?.id) {
        await api().put(`/production-targets/${progress.target.id}`, payload);
        toast.success('Target updated');
      } else {
        await api().post('/production-targets', payload);
        toast.success('Target set');
      }
      setSetOpen(false);
      fetchProgress();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const addByPt = () => setForm(f => ({ ...f, by_product_type: [...(f.by_product_type || []), { product_type_id: '', target: 1 }] }));
  const removeByPt = (i) => setForm(f => ({ ...f, by_product_type: f.by_product_type.filter((_, idx) => idx !== i) }));
  const updateByPt = (i, patch) => setForm(f => ({ ...f, by_product_type: f.by_product_type.map((b, idx) => idx === i ? { ...b, ...patch } : b) }));

  const pct = (n, d) => (d > 0 ? Math.min(100, Math.round((n / d) * 100)) : 0);
  const target = progress?.target;
  const dayMax = (progress?.day_series || []).reduce((m, x) => Math.max(m, x.completed || 0), 1);

  return (
    <Card className="industrial-card" data-testid="production-progress-widget">
      <CardHeader className="border-b border-slate-100 pb-3 flex flex-row items-center justify-between">
        <CardTitle className="text-lg font-semibold flex items-center gap-2">
          <Target className="w-5 h-5 text-orange-600" /> Production Progress
        </CardTitle>
        <div className="flex items-center gap-2">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-32 h-8 text-xs" data-testid="progress-period-select"><SelectValue /></SelectTrigger>
            <SelectContent>{PERIODS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
          </Select>
          <Button size="sm" variant="ghost" onClick={fetchProgress} title="Refresh"><RefreshCw className="w-4 h-4" /></Button>
          {isManager && <Button size="sm" variant="outline" onClick={openSetTarget} data-testid="set-target-btn">{target ? 'Edit Target' : 'Set Target'}</Button>}
          {tvLink && (
            <Popover>
              <PopoverTrigger asChild>
                <Button size="sm" variant="outline" data-testid="cast-tv-btn" title="Cast to TV"><QrCode className="w-4 h-4" /></Button>
              </PopoverTrigger>
              <PopoverContent className="w-72 text-center">
                <p className="text-sm font-semibold mb-1">Cast TV View</p>
                <p className="text-xs text-slate-500 mb-2">Scan with a shop-floor tablet or smart TV. Opens the {period} dashboard in fullscreen.</p>
                <div className="flex justify-center bg-white p-2 rounded-sm border border-slate-200">
                  <QRCodeSVG value={`${typeof window !== 'undefined' ? window.location.origin : ''}/tv?period=${period}`} size={180} />
                </div>
                <p className="text-[10px] text-slate-500 mt-2 break-all" data-testid="cast-tv-url">{`${typeof window !== 'undefined' ? window.location.origin : ''}/tv?period=${period}`}</p>
              </PopoverContent>
            </Popover>
          )}
          {tvLink && <Button size="sm" onClick={() => navigate(`/tv?period=${period}`)} className="bg-slate-900 hover:bg-slate-800" data-testid="tv-mode-btn"><Tv className="w-4 h-4 mr-1" /> TV Mode</Button>}
        </div>
      </CardHeader>
      <CardContent className="p-4 space-y-3">
        {loading && <div className="h-24 flex items-center justify-center"><div className="spinner" /></div>}
        {!loading && (
          <>
            <div className="flex items-center justify-between text-xs text-slate-500" data-testid="progress-window">
              <span>Window: <span className="font-mono">{win?.start} → {win?.end}</span></span>
              {win && <span>Day {win.days_elapsed} / {win.days_in_window}</span>}
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Stat label="Completed" value={totals.completed ?? 0} />
              <Stat label="Target" value={totals.target ?? 0} testid="progress-target-value" />
              <Stat
                label="Rate"
                value={completionRate !== null && completionRate !== undefined ? `${completionRate}%` : '—'}
                tone={completionRate === null ? 'slate' : completionRate >= 100 ? 'green' : onPace ? 'green' : 'amber'}
                testid="progress-rate-value"
              />
            </div>
            {/* Overall bar */}
            <div>
              <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                <span>Overall</span>
                <span>{totals.completed} / {totals.target || '—'}{totals.expected_pace !== null && totals.expected_pace !== undefined ? ` · pace ${totals.expected_pace.toFixed(1)}` : ''}</span>
              </div>
              <ProgressBar value={pct(totals.completed, totals.target)} pace={totals.target ? pct(totals.expected_pace, totals.target) : null} />
            </div>
            {/* Per-product breakdown */}
            {(progress?.product_breakdown || []).length > 0 && (
              <div className="space-y-1.5">
                <p className="text-xs text-slate-500">By Product Type</p>
                {progress.product_breakdown.slice(0, 6).map(b => (
                  <div key={b.product_type_id} data-testid={`progress-pt-${b.product_type_id}`}>
                    <div className="flex items-center justify-between text-xs">
                      <span className="truncate max-w-[60%]">{b.product_type_name}</span>
                      <span className="font-mono">{b.completed}{b.target ? ` / ${b.target}` : ''}</span>
                    </div>
                    <ProgressBar value={pct(b.completed, b.target)} thin />
                  </div>
                ))}
              </div>
            )}
            {/* Day chart */}
            {(progress?.day_series || []).length > 0 && (
              <div>
                <p className="text-xs text-slate-500 mb-1">Per Day</p>
                <div className="flex items-end gap-1 h-20" data-testid="progress-day-chart">
                  {progress.day_series.map(d => (
                    <div key={d.date} className="flex-1 flex flex-col items-center gap-1" title={`${d.date}: ${d.completed}`}>
                      <div className="w-full bg-orange-500 rounded-t-sm" style={{ height: `${(d.completed / dayMax) * 90 || 2}%` }} />
                      <span className="text-[9px] text-slate-500 mono">{d.date.slice(5)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {/* Top users */}
            {(progress?.top_users || []).length > 0 && (
              <div className="flex flex-wrap gap-1">
                {progress.top_users.map(u => (
                  <Badge key={u.name} variant="outline" className="text-xs">{u.name}: {u.completed}</Badge>
                ))}
              </div>
            )}
            {!target && isManager && (
              <p className="text-xs text-slate-500 border border-dashed border-slate-200 rounded-sm p-2 text-center">No target set for this {period} window. Click "Set Target" to add one.</p>
            )}
          </>
        )}
      </CardContent>

      {/* Set Target Dialog */}
      <Dialog open={setOpen} onOpenChange={setSetOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{progress?.target ? 'Edit' : 'Set'} {period.charAt(0).toUpperCase() + period.slice(1)} Target</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Window Start</Label><Input type="date" value={form.period_start || ''} onChange={e => setForm({ ...form, period_start: e.target.value })} data-testid="target-start-input" /></div>
              <div className="space-y-1"><Label>Window End</Label><Input type="date" value={form.period_end || ''} onChange={e => setForm({ ...form, period_end: e.target.value })} data-testid="target-end-input" /></div>
            </div>
            <div className="space-y-1">
              <Label>Total Target (Final Products)</Label>
              <Input type="number" min="0" value={form.target_count_total} onChange={e => setForm({ ...form, target_count_total: e.target.value })} data-testid="target-total-input" />
            </div>
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <Label>Per-Product Targets (optional)</Label>
                <Button size="sm" variant="outline" onClick={addByPt} data-testid="target-add-pt-btn"><Plus className="w-4 h-4 mr-1" /> Add</Button>
              </div>
              {(form.by_product_type || []).map((b, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-end" data-testid={`target-pt-row-${idx}`}>
                  <div className="col-span-7">
                    <Select value={b.product_type_id || 'none'} onValueChange={v => updateByPt(idx, { product_type_id: v === 'none' ? '' : v })}>
                      <SelectTrigger><SelectValue placeholder="Product type" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— select —</SelectItem>
                        {productTypes.map(pt => <SelectItem key={pt.id} value={pt.id}>{pt.product_type_name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-4"><Input type="number" min="0" value={b.target} onChange={e => updateByPt(idx, { target: parseInt(e.target.value) || 0 })} placeholder="Qty" /></div>
                  <Button size="sm" variant="ghost" onClick={() => removeByPt(idx)} className="text-red-600 col-span-1"><Trash2 className="w-4 h-4" /></Button>
                </div>
              ))}
            </div>
            <div className="space-y-1"><Label>Notes</Label><Textarea value={form.notes || ''} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSetOpen(false)}>Cancel</Button>
            <Button onClick={saveTarget} className="bg-orange-500 hover:bg-orange-600" data-testid="target-save-btn">Save Target</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

const Stat = ({ label, value, tone = 'slate', testid }) => {
  const cls = { slate: 'text-slate-700 bg-slate-50', green: 'text-emerald-700 bg-emerald-50', amber: 'text-amber-700 bg-amber-50' }[tone] || 'text-slate-700 bg-slate-50';
  return (
    <div className={`rounded-sm p-3 ${cls}`} data-testid={testid}>
      <p className="text-xs uppercase tracking-wide opacity-70">{label}</p>
      <p className="text-2xl font-bold">{value}</p>
    </div>
  );
};

const ProgressBar = ({ value, pace = null, thin = false }) => (
  <div className={`relative ${thin ? 'h-1.5' : 'h-2.5'} bg-slate-200 rounded-full overflow-hidden`}>
    <div className="absolute inset-y-0 left-0 bg-orange-500 transition-all" style={{ width: `${value}%` }} />
    {pace !== null && pace !== undefined && (
      <div className="absolute inset-y-0 w-0.5 bg-slate-700" style={{ left: `${pace}%` }} title={`Expected pace: ${pace}%`} />
    )}
  </div>
);

export default ProductionProgressWidget;
